import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
};

const api = axios.create({
    baseURL: API
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            window.location.href = '/';
        }
        return Promise.reject(error);
    }
);

// Dashboard
export const getDashboardStats = () => api.get('/dashboard/stats');

// Products
export const getProducts = () => api.get('/products');
export const getProduct = (id) => api.get(`/products/${id}`);
export const createProduct = (data) => api.post('/products', data);
export const updateProduct = (id, data) => api.put(`/products/${id}`, data);
export const deleteProduct = (id) => api.delete(`/products/${id}`);
export const bulkDeleteProducts = (ids) => api.post('/products/bulk-delete', { ids });
export const bulkUpdateCategory = (ids, category) => api.post('/products/bulk-category', { ids, category });

// Variants
export const getVariants = (productId) => api.get(`/products/${productId}/variants`);
export const createVariant = (productId, data) => api.post(`/products/${productId}/variants`, data);
export const updateVariant = (productId, variantId, data) => api.put(`/products/${productId}/variants/${variantId}`, data);
export const deleteVariant = (productId, variantId) => api.delete(`/products/${productId}/variants/${variantId}`);

// Product Movements
export const getProductMovements = (productId) => api.get(`/products/${productId}/movements`);

// Product Image
export const uploadProductImage = (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/products/${productId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    });
};
export const deleteProductImage = (productId) => api.delete(`/products/${productId}/image`);

// Categories
export const getCategories = () => api.get('/categories');
export const createCategory = (data) => api.post('/categories', data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// Warehouses
export const getWarehouses = () => api.get('/warehouses');
export const createWarehouse = (data) => api.post('/warehouses', data);
export const updateWarehouse = (id, data) => api.put(`/warehouses/${id}`, data);
export const deleteWarehouse = (id) => api.delete(`/warehouses/${id}`);

// Movements
export const getMovements = () => api.get('/movements');
export const createMovement = (data) => api.post('/movements', data);
export const deleteMovement = (id) => api.delete(`/movements/${id}`);

// Stock
export const getStock = () => api.get('/stock');
export const getStockDetailed = () => api.get('/stock/detailed');
export const adjustStock = (data) => api.post('/stock/adjust', data);
export const getStockReport = () => api.get('/stock/report');
export const getRecentMovements = () => api.get('/movements/recent');

// Employees
export const getEmployees = () => api.get('/employees');
export const createEmployee = (data) => api.post('/employees', data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data);
export const deleteEmployee = (id) => api.delete(`/employees/${id}`);

// Roles
export const getRoles = () => api.get('/roles');
export const createRole = (data) => api.post('/roles', data);
export const updateRole = (id, data) => api.put(`/roles/${id}`, data);
export const deleteRole = (id) => api.delete(`/roles/${id}`);

// Customers
export const getCustomers = () => api.get('/customers');
export const getCustomer = (id) => api.get(`/customers/${id}`);
export const createCustomer = (data) => api.post('/customers', data);
export const updateCustomer = (id, data) => api.put(`/customers/${id}`, data);
export const deleteCustomer = (id) => api.delete(`/customers/${id}`);
export const deleteAllCustomers = () => api.delete('/customers');

// Orders
export const getOrders = () => api.get('/orders');
export const getOrder = (id) => api.get(`/orders/${id}`);
export const createOrder = (data) => api.post('/orders', data);
export const updateOrder = (id, data) => api.put(`/orders/${id}`, data);
export const deleteOrder = (id) => api.delete(`/orders/${id}`);
export const deductOrderStock = (id) => api.post(`/orders/${id}/deduct-stock`);
export const getNextOrderNumber = () => api.get('/orders/next-number/get');

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);
export const uploadCompanyLogo = (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/settings/logo', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const deleteCompanyLogo = () => api.delete('/settings/logo');

// PDF
export const getOrderPDF = (id) => api.get(`/orders/${id}/pdf`, { responseType: 'blob' });
export const getPackingPDF = (id) => api.get(`/orders/${id}/packing-pdf`, { responseType: 'blob' });

// Billit
export const getBillitConfig = () => api.get('/billit/config');
export const sendBillitDeliveryNote = (orderId) => api.post(`/billit/delivery-note/${orderId}`);
export const getBillitDeliveryNoteStatus = (orderId) => api.get(`/billit/delivery-note/${orderId}/status`);
export const importBillitCustomers = () => api.post('/billit/import-customers');
export const getBillitInvoices = () => api.get('/billit/invoices');

// Attendance
export const clockInOut = (companySlug, pin) => api.post(`/attendance/clock/${companySlug}`, { pin });
export const getAttendanceStatus = (date) => api.get('/attendance/status', { params: date ? { date } : {} });
export const getAttendanceLogs = (params) => api.get('/attendance/logs', { params });
export const deleteAttendanceLog = (id) => api.delete(`/attendance/logs/${id}`);
export const updateAttendanceLog = (id, data) => api.put(`/attendance/logs/${id}`, data);
export const getAttendanceReport = (month, employeeId) => api.get('/attendance/report', { params: { month, employee_id: employeeId } });
export const getCompanyBySlug = (slug) => api.get(`/attendance/company/${slug}`);

// Workers
export const getWorkers = () => api.get('/workers');
export const createWorker = (data) => api.post('/workers', data);
export const updateWorker = (id, data) => api.put(`/workers/${id}`, data);
export const deleteWorker = (id) => api.delete(`/workers/${id}`);

// SuperAdmin
export const getAllCompanies = () => api.get('/superadmin/companies');
export const deleteCompany = (id) => api.delete(`/superadmin/companies/${id}`);
export const getSuperAdminStats = () => api.get('/superadmin/stats');

export default api;
